import React, { useState, useEffect } from "react";
import {
  Autocomplete,
  TextField,
  Box,
  Typography,
  IconButton,
  Button,
  Divider,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import useBundleableProducts from "../hooks/useBundleableProducts";
import ImageThumb from "./ImageThumb";

const optionSelectionsFor = (product) => {
  const option = product?.options?.[0];
  if (!option || !option.values?.length) return [];
  return [{ componentOptionId: option.id, name: option.name, values: [option.values[0]] }];
};

const DEFAULT_SIZES = [
  { quantity: 1, imageFile: null, imagePreview: null },
  { quantity: 2, imageFile: null, imagePreview: null },
];

const sizeName = (units) =>
  units === "" || units === null || units === undefined ? "" : `${units} Pack`;

export default function BundleProductSelector({ type, onChange, onVariantImagesChange = () => {} }) {
  const [search, setSearch] = useState("");
  const { options, loading } = useBundleableProducts(search);

  // FIXED: [{ product, quantity }]
  const [items, setItems] = useState([]);
  // PACK: single base product + its sizes ({ name, quantity, imageFile, imagePreview })
  const [baseProduct, setBaseProduct] = useState(null);
  const [sizes, setSizes] = useState(DEFAULT_SIZES);

  useEffect(() => {
    setItems([]);
    setBaseProduct(null);
    setSizes(DEFAULT_SIZES);
  }, [type]);

  const handleSizeImage = (idx, e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setSizes((prev) =>
      prev.map((x, i) =>
        i === idx ? { ...x, imageFile: file, imagePreview: URL.createObjectURL(file) } : x
      )
    );
  };

  useEffect(() => {
    if (type === "PACK") {
      if (!baseProduct) return onChange([]);
      const values = sizes
        .filter((s) => Number(s.quantity) > 0)
        .map((s) => ({ name: sizeName(s.quantity), quantity: Number(s.quantity) }));
      onChange(
        values.length
          ? [
              {
                productId: baseProduct.id,
                optionSelections: optionSelectionsFor(baseProduct),
                quantityOption: { name: "Pack Size", values },
              },
            ]
          : []
      );
    } else {
      onChange(
        items
          .filter((it) => Number(it.quantity) > 0)
          .map((it) => ({
            productId: it.product.id,
            optionSelections: optionSelectionsFor(it.product),
            quantity: Number(it.quantity),
          }))
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, items, baseProduct, sizes]);

  // Emit the per-variant image files (PACK only), matched later by size name.
  useEffect(() => {
    if (type === "PACK") {
      onVariantImagesChange(
        sizes
          .filter((s) => s.imageFile && Number(s.quantity) > 0)
          .map((s) => ({ name: sizeName(s.quantity), file: s.imageFile }))
      );
    } else {
      onVariantImagesChange([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, sizes]);

  const renderSearch = (label, onPick, selectedIds = []) => (
    <Autocomplete
      options={options.filter((o) => !selectedIds.includes(o.id))}
      loading={loading}
      filterOptions={(x) => x}
      getOptionLabel={(o) => o.title || ""}
      value={null}
      blurOnSelect
      onChange={(e, value) => value && onPick(value)}
      onInputChange={(e, value) => setSearch(value)}
      renderOption={(props, option) => (
        <Box component="li" sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1 }} {...props}>
          <ImageThumb src={option.image} alt={option.title} size={40} />
          <Typography variant="body2">{option.title}</Typography>
        </Box>
      )}
      renderInput={(params) => (
        <TextField {...params} label={label} placeholder="Search products…" size="small" />
      )}
    />
  );

  // ----- Single Product (PACK): one base product + sizes -----
  if (type === "PACK") {
    return (
      <Box>
        {!baseProduct ? (
          renderSearch("Base product", (product) => setBaseProduct(product))
        ) : (
          <>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
              <ImageThumb src={baseProduct.image} alt={baseProduct.title} size={48} zoomable />
              <Typography variant="body2" sx={{ flex: 1, fontWeight: 600 }}>
                {baseProduct.title}
              </Typography>
              <Button size="small" onClick={() => setBaseProduct(null)}>
                Change
              </Button>
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Sizes (each one becomes a variant)
            </Typography>
            {sizes.map((s, idx) => (
              <Box key={idx} sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
                <TextField
                  label="Units"
                  type="number"
                  size="small"
                  value={s.quantity}
                  onChange={(e) =>
                    setSizes((prev) =>
                      prev.map((x, i) => (i === idx ? { ...x, quantity: e.target.value } : x))
                    )
                  }
                  inputProps={{ min: 1 }}
                  sx={{ width: 120 }}
                />
                <TextField
                  label="Name"
                  size="small"
                  value={sizeName(s.quantity)}
                  disabled
                  sx={{ width: 160 }}
                />
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <ImageThumb src={s.imagePreview} alt={sizeName(s.quantity)} size={40} zoomable />
                  <Button component="label" size="small" sx={{ minWidth: 0 }}>
                    {s.imagePreview ? "Change image" : "Upload image"}
                    <input
                      type="file"
                      accept="image/*"
                      hidden
                      onChange={(e) => handleSizeImage(idx, e)}
                    />
                  </Button>
                  {s.imagePreview && (
                    <Button
                      size="small"
                      sx={{ minWidth: 0 }}
                      onClick={() =>
                        setSizes((prev) =>
                          prev.map((x, i) =>
                            i === idx
                              ? { ...x, imageFile: null, imagePreview: null }
                              : x
                          )
                        )
                      }
                    >
                      Remove
                    </Button>
                  )}
                </Box>
                <IconButton
                  size="small"
                  onClick={() => setSizes((prev) => prev.filter((_, i) => i !== idx))}
                  disabled={sizes.length <= 2}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() =>
                setSizes((prev) => [
                  ...prev,
                  { quantity: "", imageFile: null, imagePreview: null },
                ])
              }
              sx={{ mt: 1 }}
            >
              Add presentation
            </Button>
          </>
        )}
      </Box>
    );
  }

  // ----- Different Products (FIXED): several products with quantities -----
  return (
    <Box>
      {renderSearch(
        "Add product",
        (product) => setItems((prev) => [...prev, { product, quantity: 1 }]),
        items.map((it) => it.product.id)
      )}

      {items.length > 0 && (
        <Box sx={{ mt: 2 }}>
          {items.map((it, idx) => (
            <Box key={it.product.id}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, py: 1.5 }}>
                <ImageThumb src={it.product.image} alt={it.product.title} size={48} />
                <Typography variant="body2" sx={{ flex: 1 }}>
                  {it.product.title}
                </Typography>
                <TextField
                  label="Qty"
                  type="number"
                  size="small"
                  value={it.quantity}
                  onChange={(e) =>
                    setItems((prev) =>
                      prev.map((x, i) => (i === idx ? { ...x, quantity: e.target.value } : x))
                    )
                  }
                  inputProps={{ min: 1 }}
                  sx={{ width: 100 }}
                />
                <IconButton
                  size="small"
                  onClick={() => setItems((prev) => prev.filter((_, i) => i !== idx))}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
              {idx < items.length - 1 && <Divider />}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}
